import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "convex/react";
import { useUser } from "@clerk/clerk-react";
import { api } from "../../convex/_generated/api";
import { ComputerCard } from "../components/configurator/ComputerCard";
import { 
  Monitor, 
  Download, 
  ArrowRight, 
  ShieldCheck, 
  AlertCircle, 
  RefreshCw, 
  Copy, 
  Check
} from "lucide-react";

/**
 * DashboardErrorBoundary Component
 * 
 * Captures any unexpected runtime errors (such as Convex query failures) in the 
 * child dashboard content tree and renders a stylized, non-blocking error UI.
 * This keeps the application shell intact and allows users to reload safely.
 */
class DashboardErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("DashboardErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="dashboard-error"
          style={{
            maxWidth: "600px",
            margin: "4rem auto",
            padding: "2.5rem 2rem",
            backgroundColor: "var(--color-bg-secondary)",
            border: "1px solid var(--color-error)",
            borderRadius: "var(--border-radius-lg)",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1.25rem",
            boxShadow: "0 4px 20px rgba(239, 68, 68, 0.1)"
          }}
        >
          <AlertCircle size={48} color="var(--color-error)" />
          <h2 style={{ fontSize: "1.5rem", fontWeight: 700 }}>Unable to Load Dashboard</h2>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem", lineHeight: "1.5" }}>
            {this.state.error?.message || "An unexpected error occurred while communicating with the database."}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "var(--color-bg-primary)",
              color: "var(--color-text-primary)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--border-radius-md)",
              padding: "0.65rem 1.5rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "border-color 0.2s"
            }}
            onMouseOver={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
            onMouseOut={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
          >
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * OnboardingSteps Component
 * 
 * Renders a high-fidelity interactive step-by-step checklist to guide 
 * newly signed-up users through pairing their first computers.
 */
const OnboardingSteps: React.FC<{ userEmail: string }> = ({ userEmail }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(userEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      data-testid="dashboard-empty"
      style={{
        backgroundColor: "var(--color-bg-secondary)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--border-radius-lg)",
        padding: "3rem 2rem",
        display: "flex",
        flexDirection: "column",
        gap: "2.5rem",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)"
      }}
    >
      {/* Title Header */}
      <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <h3 style={{ fontSize: "1.5rem", fontWeight: 800 }} className="outfit-font">
          No computers paired yet
        </h3>
        <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem", maxWidth: "550px", margin: "0 auto", lineHeight: "1.6" }}>
          Welcome to MultiKeys! Follow this quick 4-step onboarding checklist to link your computers and share your controls in under 2 minutes.
        </p>
      </div>

      {/* Grid of Steps */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "1.5rem"
      }}>
        {/* Step 1: Download client */}
        <div style={{
          backgroundColor: "var(--color-bg-primary)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius-md)",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          position: "relative"
        }}>
          <span style={{
            position: "absolute",
            top: "-12px",
            left: "15px",
            backgroundColor: "var(--color-accent)",
            color: "var(--color-bg-primary)",
            fontWeight: 800,
            fontSize: "0.75rem",
            padding: "0.15rem 0.5rem",
            borderRadius: "4px"
          }}>STEP 1</span>
          
          <h4 style={{ fontSize: "1.05rem", fontWeight: 700, marginTop: "0.25rem" }}>Download Desktop App</h4>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.85rem", lineHeight: "1.5", flexGrow: 1 }}>
            Download and install the MultiKeys client application on each computer.
          </p>
          
          <Link
            to="/download"
            style={{
              backgroundColor: "var(--color-accent)",
              color: "var(--color-bg-primary)",
              padding: "0.6rem 1rem",
              borderRadius: "var(--border-radius-sm)",
              fontWeight: 700,
              fontSize: "0.85rem",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.4rem",
              marginTop: "auto"
            }}
            data-testid="empty-state-download-btn"
          >
            <Download size={14} />
            Download App
          </Link>
        </div>

        {/* Step 2: Login */}
        <div style={{
          backgroundColor: "var(--color-bg-primary)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius-md)",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          position: "relative"
        }}>
          <span style={{
            position: "absolute",
            top: "-12px",
            left: "15px",
            backgroundColor: "var(--color-accent)",
            color: "var(--color-bg-primary)",
            fontWeight: 800,
            fontSize: "0.75rem",
            padding: "0.15rem 0.5rem",
            borderRadius: "4px"
          }}>STEP 2</span>
          
          <h4 style={{ fontSize: "1.05rem", fontWeight: 700, marginTop: "0.25rem" }}>Sign In on Devices</h4>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.85rem", lineHeight: "1.5", flexGrow: 1 }}>
            Open the app on your machines and sign in with this exact account:
          </p>
          
          <div style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "var(--color-bg-tertiary)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--border-radius-sm)",
            padding: "0.4rem 0.6rem",
            justifyContent: "space-between",
            marginTop: "auto",
            overflow: "hidden"
          }}>
            <span style={{
              fontSize: "0.75rem",
              fontFamily: "var(--font-mono)",
              color: "var(--color-text-primary)",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              maxWidth: "140px"
            }} title={userEmail}>
              {userEmail}
            </span>
            <button
              onClick={handleCopyEmail}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                display: "inline-flex",
                color: copied ? "var(--color-success)" : "var(--color-accent)"
              }}
              title="Copy email to clipboard"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>

        {/* Step 3: Network pairing */}
        <div style={{
          backgroundColor: "var(--color-bg-primary)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius-md)",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          position: "relative"
        }}>
          <span style={{
            position: "absolute",
            top: "-12px",
            left: "15px",
            backgroundColor: "var(--color-accent)",
            color: "var(--color-bg-primary)",
            fontWeight: 800,
            fontSize: "0.75rem",
            padding: "0.15rem 0.5rem",
            borderRadius: "4px"
          }}>STEP 3</span>
          
          <h4 style={{ fontSize: "1.05rem", fontWeight: 700, marginTop: "0.25rem" }}>Auto-Pair & Sync</h4>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.85rem", lineHeight: "1.5", flexGrow: 1 }}>
            Once you log in, the machines will automatically link and sync over your local network.
          </p>

          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "auto",
            padding: "0.4rem 0"
          }}>
            {/* Spinning network radar simulation */}
            <div style={{ position: "relative", width: "16px", height: "16px" }}>
              <div style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                border: "2px solid var(--color-accent)",
                opacity: 0.4,
                animation: "pulse-radar 1.5s infinite"
              }} />
              <div style={{
                position: "absolute",
                top: "4px",
                left: "4px",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "var(--color-accent)"
              }} />
            </div>
            <span style={{ fontSize: "0.75rem", fontFamily: "var(--font-mono)", color: "var(--color-text-secondary)" }}>
              Scanning LAN...
            </span>
          </div>
        </div>

        {/* Step 4: Configurator arrange */}
        <div style={{
          backgroundColor: "var(--color-bg-primary)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius-md)",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          position: "relative"
        }}>
          <span style={{
            position: "absolute",
            top: "-12px",
            left: "15px",
            backgroundColor: "var(--color-accent)",
            color: "var(--color-bg-primary)",
            fontWeight: 800,
            fontSize: "0.75rem",
            padding: "0.15rem 0.5rem",
            borderRadius: "4px"
          }}>STEP 4</span>
          
          <h4 style={{ fontSize: "1.05rem", fontWeight: 700, marginTop: "0.25rem" }}>Arrange Screens</h4>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.85rem", lineHeight: "1.5", flexGrow: 1 }}>
            Arrange your physical screen placements side-by-side to declare sharing boundaries.
          </p>
          
          <Link
            to="/configurator"
            style={{
              border: "1px solid var(--color-accent)",
              color: "var(--color-accent)",
              padding: "0.6rem 1rem",
              borderRadius: "var(--border-radius-sm)",
              fontWeight: 700,
              fontSize: "0.85rem",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.4rem",
              marginTop: "auto",
              transition: "background-color 0.2s"
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = "var(--color-accent-glow)"}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = "transparent"}
          >
            Configurator
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Animation keyframes style tag */}
      <style>{`
        @keyframes pulse-radar {
          0% { transform: scale(0.6); opacity: 1; }
          100% { transform: scale(1.6); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

/**
 * DashboardContent Component
 * 
 * The main body of the dashboard page. It handles:
 * 1. Fetching computers and subscription plans reactively from the backend.
 * 2. Evaluating and displaying Loading, Empty, and Success states.
 * 3. Providing links to download client binaries, update the desktop layout, and upgrade tiers.
 */
const DashboardContent: React.FC = () => {
  // Query paired computer status list reactively
  const computers = useQuery(api.computers.getUserComputers);
  
  // Query subscription plan and trial countdown details
  const planInfo = useQuery(api.users.getUserPlan);

  // Fetch Clerk user details for showing login credentials in onboarding
  const { user } = useUser();

  // --- 1. LOADING STATE ---
  // If either query returns undefined, Convex is still resolving data. Display a loading spinner.
  if (computers === undefined || planInfo === undefined) {
    return (
      <div 
        data-testid="dashboard-loading"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          gap: "1rem",
          color: "var(--color-text-secondary)"
        }}
      >
        <RefreshCw 
          className="animate-spin" 
          size={36} 
          color="var(--color-accent)" 
          style={{ animation: "spin 1s linear infinite" }}
        />
        <p style={{ fontSize: "1rem", fontWeight: 500 }}>Syncing paired machines...</p>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // --- PRE-COMPUTE PLAN LAYOUT CONSTANTS ---
  const isTrial = planInfo.plan === "trial";
  const isFree = planInfo.plan === "free";
  const isPro = planInfo.plan === "pro";
  const isTeam = planInfo.plan === "team";
  
  // Counts of online devices
  const totalCount = computers.length;
  const onlineCount = computers.filter(c => c.online).length;

  // Primary user email to guide onboarding login
  const emailAddress = user?.primaryEmailAddress?.emailAddress || "your account email";

  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "2rem 1.5rem", display: "flex", flexDirection: "column", gap: "2rem" }}>
      
      {/* 2. PLAN & SUBSCRIPTION BANNER */}
      <section 
        data-testid="plan-status-banner"
        style={{
          backgroundColor: "var(--color-bg-secondary)",
          border: isFree ? "1px solid var(--color-warning)" : "1px solid var(--color-border)",
          borderRadius: "var(--border-radius-lg)",
          padding: "1.5rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1.5rem",
          boxShadow: isTrial ? "0 0 15px var(--color-accent-glow)" : "none"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {isFree ? (
            <AlertCircle size={28} color="var(--color-warning)" />
          ) : (
            <ShieldCheck size={28} color="var(--color-success)" />
          )}
          <div>
            <h3 style={{ fontSize: "1.15rem", fontWeight: 700, textTransform: "capitalize", margin: 0 }}>
              {planInfo.plan} Account Status
            </h3>
            <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", marginTop: "0.25rem" }}>
              {isTrial && planInfo.trialDaysLeft !== undefined && (
                `Your free trial has ${planInfo.trialDaysLeft} days remaining. Connect unlimited devices.`
              )}
              {isFree && (
                "Limited to 2 computers. Connect a third machine by upgrading your account."
              )}
              {isPro && (
                "Pro Plan active. Unlimited connected computers & premium glow cursor enabled."
              )}
              {isTeam && (
                "Team Workspace active. Dedicated seat licenses enabled."
              )}
            </p>
          </div>
        </div>

        {/* Action button for non-paid users */}
        {(isFree || isTrial) && (
          <Link
            to="/billing"
            style={{
              backgroundColor: "var(--color-accent)",
              color: "var(--color-bg-primary)",
              padding: "0.65rem 1.5rem",
              borderRadius: "var(--border-radius-md)",
              fontWeight: 700,
              fontSize: "0.9rem",
              boxShadow: "0 0 10px rgba(0, 212, 255, 0.2)",
              transition: "all 0.2s ease"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-accent-hover)";
              e.currentTarget.style.boxShadow = "0 0 15px var(--color-accent)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-accent)";
              e.currentTarget.style.boxShadow = "0 0 10px rgba(0, 212, 255, 0.2)";
            }}
            data-testid="upgrade-cta"
          >
            Upgrade Plan
          </Link>
        )}
      </section>

      {/* 3. LAYOUT CONFIGURATOR PREVIEW LINK */}
      <section 
        data-testid="layout-preview-card"
        style={{
          backgroundColor: "var(--color-bg-secondary)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius-lg)",
          padding: "1.5rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1.5rem"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Monitor size={28} color="var(--color-accent)" />
          <div>
            <h3 style={{ fontSize: "1.15rem", fontWeight: 700, margin: 0 }}>
              Keyboard & Mouse Layout
            </h3>
            <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", marginTop: "0.25rem" }}>
              Arrange your physical monitor mapping on the canvas grid to dictate edge crossover boundaries.
            </p>
          </div>
        </div>

        <Link
          to="/configurator"
          style={{
            color: "var(--color-accent)",
            fontWeight: 600,
            fontSize: "0.95rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
          onMouseOver={(e) => (e.currentTarget.style.color = "var(--color-accent-hover)")}
          onMouseOut={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
          data-testid="configure-layout-link"
        >
          Open Configurator
          <ArrowRight size={16} />
        </Link>
      </section>

      {/* 4. MAIN DEVICES CONTAINER */}
      <section style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 700 }}>Paired Devices</h2>
          {totalCount > 0 && (
            <span style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", fontWeight: 500 }}>
              {onlineCount} of {totalCount} online
            </span>
          )}
        </div>

        {/* --- EMPTY STATE: RENDER STEP-BY-STEP CHECKLIST --- */}
        {totalCount === 0 ? (
          <OnboardingSteps userEmail={emailAddress} />
        ) : (
          /* --- SUCCESS STATE: CARD GRID --- */
          <div 
            data-testid="computers-grid"
            style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", 
              gap: "1.5rem" 
            }}
          >
            {computers.map((comp) => (
              <ComputerCard key={comp._id} computer={comp} />
            ))}

            {/* If user is free and has reached the cap of 2, show a promo card instead of install guide */}
            {isFree && totalCount >= 2 ? (
              <div
                data-testid="device-limit-card"
                style={{
                  backgroundColor: "var(--color-bg-tertiary)",
                  border: "1px dashed var(--color-warning)",
                  borderRadius: "var(--border-radius-lg)",
                  padding: "1.5rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  gap: "1rem"
                }}
              >
                <AlertCircle size={32} color="var(--color-warning)" />
                <div>
                  <h4 style={{ fontSize: "1.05rem", fontWeight: 700, margin: 0 }}>Device Limit Reached</h4>
                  <p style={{ color: "var(--color-text-secondary)", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                    Free accounts are capped at 2 devices. Upgrade to Pro for unlimited connections.
                  </p>
                </div>
                <Link
                  to="/billing"
                  style={{
                    color: "var(--color-accent)",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem"
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "var(--color-accent-hover)")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
                >
                  View Pricing
                  <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              /* Inline Pairing CTA Helper card */
              <div
                data-testid="add-device-cta-card"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  border: "1px dashed var(--color-border)",
                  borderRadius: "var(--border-radius-lg)",
                  padding: "1.5rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  gap: "1rem"
                }}
              >
                <Download size={32} color="var(--color-text-muted)" />
                <div>
                  <h4 style={{ fontSize: "1.05rem", fontWeight: 700, margin: 0 }}>Need to add a machine?</h4>
                  <p style={{ color: "var(--color-text-secondary)", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                    Install and login with this account on your other computer to link it.
                  </p>
                </div>
                <Link
                  to="/download"
                  style={{
                    color: "var(--color-accent)",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem"
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "var(--color-accent-hover)")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
                >
                  Download App
                  <ArrowRight size={14} />
                </Link>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

/**
 * DashboardPage Container
 * 
 * Renders the dashboard screen wrapped in a boundary container to gracefully handle 
 * and display any query or connection errors.
 */
export const DashboardPage: React.FC = () => {
  return (
    <div 
      style={{ 
        backgroundColor: "var(--color-bg-primary)", 
        color: "var(--color-text-primary)", 
        minHeight: "80vh",
        padding: "1rem 0"
      }}
      data-testid="dashboard-page"
    >
      <DashboardErrorBoundary>
        <DashboardContent />
      </DashboardErrorBoundary>
    </div>
  );
};

export default DashboardPage;
